import React, { useCallback, useRef } from 'react';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import {
  Table, TableRow, TableCell, Button, Typography, Box,
} from '@mui/material';
import { MagicWand01 } from '../../../assets';
import OrderFormRow from './OrderFormRow';
import { theme } from '../../../theme';
import { OrderForm, OrderFormUpdate, OrderFormItem, OrderFormCategory, DeliveryDates, OrderFormItemUpdate } from '../../../app/services/api/orderManagementClient';
import { TFunction } from 'i18next';
import { PredictedDemandModel } from '../../../app/services/api/podClient';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import { getDayOfWeek } from '../../../utils';
import { useTranslation } from 'react-i18next';

interface VirtualizedTableProps {
  getImStockQuantity: (item: OrderFormItem) => number;
  loading: boolean;
  showImage: boolean;
  orderForm?: OrderForm;
  orderType: number;
  displayedDates: DeliveryDates[];
  predictedDemands: PredictedDemandModel[];
  matchPredictedQuantities: (dateIndex: number) => void;
  handleMagicWandClicked: (predictedQuantity: number, item: OrderFormItem, index: number) => void;
  orderFormUpdates: OrderFormUpdate;
  handleSingleQuantityChange: (
    newQuantity: number,
    sku: string,
    itemId: number,
    dateIndex: number,
  ) => Promise<void>;
  canSaveOrder: boolean;
  isQuantityError: (item: OrderFormItem, currentValue: string) => boolean;
  quantityError: (item: OrderFormItem, currentValue: string) => string | null;
  showMessageWhenNoCost: boolean;
  currencyCode: string;
  t: TFunction;
  isLargeScreen: boolean;
  usePagination: boolean;
  page: number;
  totalPages: number;
  handlePageChange: (newPage: number) => void;
  dirtyRef: React.MutableRefObject<boolean>;
}

const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  getImStockQuantity,
  loading,
  showImage,
  orderForm,
  orderType,
  displayedDates,
  predictedDemands,
  matchPredictedQuantities,
  handleMagicWandClicked,
  orderFormUpdates,
  handleSingleQuantityChange,
  canSaveOrder,
  isQuantityError,
  quantityError,
  showMessageWhenNoCost,
  currencyCode,
  t,
  isLargeScreen,
  dirtyRef,
}) => {
  const updatesRef = useRef<OrderFormItemUpdate[]>([]);
  const { t: tCommon } = useTranslation('common');

  const mergeUpdates = (target: OrderFormItemUpdate, incoming: OrderFormItemUpdate): OrderFormItemUpdate => {
    // merge dates with incoming taking precedence
    const acc: Record<string, number> = {};
    for (const d of target.dates ?? []) acc[d.date] = d.quantity;
    for (const d of incoming.dates ?? []) acc[d.date] = d.quantity;

    const dates = Object.keys(acc)
      .sort()
      .map(date => ({ date, quantity: acc[date] }));

    return {
      ...target,
      ...incoming,
      id: target.id ?? incoming.id,
      sku: target.sku ?? incoming.sku,
      dates,
    };
  };

  const upsertOrderFormItemUpdate = (single: OrderFormItemUpdate) => {
    dirtyRef.current = true;
    const arr = updatesRef.current;
    const idx = arr.findIndex(it =>
      (single.id && it.id === single.id) || (single.sku && it.sku === single.sku),
    );
    if (idx === -1) {
      updatesRef.current = [...arr, {
        ...single,
        dates: Object.keys(Object.assign({}, ...single.dates.map(d => ({ [d.date]: d.quantity }))))
          .sort()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map(date => ({ date, quantity: (Object.assign({}, ...single.dates.map(d => ({ [d.date]: d.quantity }))) as any)[date] })),
      }];
      return;
    }
    const next = arr.slice();
    next[idx] = mergeUpdates(arr[idx], single);
    updatesRef.current = next;
  };

  const getItemUpdate = useCallback((item: OrderFormItem) => {
    const arr = updatesRef.current;
    return arr.find(u => (u.id && u.id === item.id) || (u.sku && u.sku === item.sku));
  }, []);

  if (loading) {
    return null;
  }

  const categories: OrderFormCategory[] = orderForm?.categories ?? [];
  // Each group = one category, groupCounts holds number of item rows per category
  const groupCounts = categories.map(c => c.items?.length ?? 0);
  const hasPredictions = predictedDemands.length > 0;
  const headerHeight = hasPredictions ? 108 : 68;

  return (
    <GroupedTableVirtuoso
      style={{
        height: 'max(95vh, 400px)',
        transform: 'none', // <— prevents Virtuoso wrapper from creating a new stacking context
        border: '0px',
      }}
      overscan={20}
      groupCounts={groupCounts}
      components={{
        Table: (props) => (
          <Table
            {...props}
            size="small"
            sx={{
              tableLayout: 'auto',
              minWidth: 900,
              border: '0px',
            }}
            aria-label="order form table"
          />
        ),
        TableRow: TableRow,
      }}
      fixedHeaderContent={() => (
        <>
          <TableRow sx={{
            margin: 0,
            position: 'sticky',
            top: 0,
          }}>
            <TableCell
              rowSpan={2}
              sx={{
                width: '40%',
                maxWidth: '40%',
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: theme.palette.custom.gray[200],
                borderBottom: '0px solid',
                borderColor: theme.palette.custom.gray[200],
                verticalAlign: 'middle',
                px: 2,
                py: 0.5,
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 130px', gridTemplateRows: '42px 1fr 1fr', columnGap: 2, height: '100%' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '12px', alignSelf: 'start', gridColumn: 1, gridRow: 1 }}>
                  {t('productInformation')}
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '12px', lineHeight: 1, alignSelf: 'center', gridColumn: 2, gridRow: 2 }}>
                  {t('deliveryDate')}
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '12px', lineHeight: 1, alignSelf: 'center', gridColumn: 2, gridRow: 3 }}>
                  {t('cutOff')}
                </Typography>
              </Box>
            </TableCell>
            {displayedDates.map((item, index) => (
              <TableCell
                key={index}
                align="left"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.custom.gray[200],
                  borderBottom: '0px solid',
                  borderColor: theme.palette.custom.gray[200],
                  pt: 0,
                  pb: 0,
                }}
              >
                <Box>
                  {hasPredictions &&
                    <Button
                      fullWidth
                      variant="primary"
                      disabled={loading}
                      startIcon={<MagicWand01 style={{ width: 20, height: 20 }} />}
                      size="md"
                      style={{ fontSize: 12 }}
                      sx={{ mb: 0.75 }}
                      onClick={() => matchPredictedQuantities(index)}
                    >
                      {t('applyAI')}
                    </Button>
                  }
                  <Typography
                    component="span"
                    display="block"
                    sx={{ fontSize: '12px', lineHeight: 1 }}
                  >
                    {getDayOfWeek(item.deliveryDate, tCommon)}
                  </Typography>
                  <Typography
                    component="span"
                    display="block"
                    sx={{ fontSize: '12px', lineHeight: 1 }}
                  >
                    {item.deliveryDate}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
          <TableRow sx={{
            margin: 0,
            position: 'sticky',
            top: 0,
          }}>
            {displayedDates.map((item, index) => (
              <TableCell
                key={`cutoff-${index}`}
                align="left"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.custom.gray[200],
                  borderBottom: '0px solid',
                  borderColor: theme.palette.custom.gray[200],
                  pt: 0,
                  pb: 0.5,
                }}
              >
                <Typography component="span"
                  sx={{ fontSize: '12px', lineHeight: 1 }}>
                  {item.cutOffTime}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </>
      )}
      groupContent={(groupIndex) => {
        const category = categories[groupIndex];
        return (
          <TableCell
            key={`category-${orderType}-${category.id}`}
            colSpan={displayedDates.length + 1}
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 3,
              py: 2,
              position: 'sticky',
              top: headerHeight,
              zIndex: 1,
            }}
          >
            <Typography variant="textSM"
              color="white">
              {isOrderCategory(category.name)
                ? t(
                  OrderCategoriesTranslationMap[
                    category.name as keyof typeof OrderCategoriesTranslationMap
                  ],
                )
                : category.name}
            </Typography>
          </TableCell>
        );
      }}
      itemContent={(itemIndex, groupIndex) => {
        const category = categories[groupIndex];
        const offset = groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0);
        const item = category.items?.[itemIndex - offset];
        if (!item) {
          return null;
        }

        return (
          <OrderFormRow
            key={`categoryItem-${orderType}-${category.id}-${item.id}`}
            orderType={orderType}
            item={item}
            imStockOnHand={getImStockQuantity(item)}
            dates={displayedDates}
            getItemUpdate={getItemUpdate}
            handleSingleQuantityChange={handleSingleQuantityChange}
            canSaveOrder={canSaveOrder}
            isQuantityError={isQuantityError}
            quantityError={quantityError}
            showMessageWhenNoCost={showMessageWhenNoCost}
            currencyCode={currencyCode}
            t={t}
            predictedDemands={predictedDemands}
            handleMagicWandClicked={(predictedQty, itm, idx) => handleMagicWandClicked(predictedQty, itm, idx)}
            upsertOrderFormItemUpdate={upsertOrderFormItemUpdate}
          />
        );
      }}
    />
  );
};

export default VirtualizedTable;